import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { User } from 'src/app/models/user.model';
import { ApiService, AuthService, PermissionsService, UserService, UtilsService } from 'src/app/services';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix, MatPrefix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { NgOptimizedImage } from '@angular/common';
import { RemoteWorkDaysPipe } from 'src/app/pipes/remote-work-days.pipe';

@Component({
  selector: 'app-users',
  templateUrl: './workers.component.html',
  styleUrls: ['./workers.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    RouterLink,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatPrefix,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatTooltip,
    NgOptimizedImage,
    RemoteWorkDaysPipe,
  ],
})
export class WorkersComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly destroy_ref = inject(DestroyRef);
  public dialog = inject(MatDialog);
  protected fb = inject(UntypedFormBuilder);
  public router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);
  public permissions = inject(PermissionsService);
  protected utils = inject(UtilsService);

  displayedColumns: string[] = [
    'profile-pic',
    'serious-pic',
    'name',
    'email',
    'company_products',
    'homeOfficeDay',
    'hollidays',
    'categories',
    'actions',
  ];
  dataSource: MatTableDataSource<User> = new MatTableDataSource<User>();
  filterForm: UntypedFormGroup;

  override filterFormExtraParams: Record<string, unknown> = {
    scopes: [this.activatedRoute.snapshot.queryParamMap.get('scopes') ?? 'activeWorkers'],
  };

  protected readonly me = this.userService.getCurrentUser();

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.processUrlParams();

    if (!this.activatedRoute.snapshot.queryParamMap.get('page')) {
      this.pageIndex = 0;
    }

    super.ngOnInit();
    this.listenScopesFormControl();
  }

  impersonateUser(index: number) {
    const user = this.dataSource.data[index];
    this.auth.impersonate(user.id).subscribe(() => {
      this.router.navigate(['/']);
    });
  }

  override changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
    super.changePage(event);
  }

  override fetchData() {
    const scopes = this.filterForm.value.scopes ?? 'activeWorkers';
    const append = scopes === 'activeWorkers' ? 'active_company_products,vacation_days_with_pending' : 'active_company_products';

    this.api
      .getUsers({
        search: this.filterForm.value.search,
        scopes,
        page: this.pageIndex + 1,
        per_page: this.pageSize,
        with: 'role,current_working_contract..current_working_contract_variable_condition..department_categories,current_working_contract..current_working_contract_weekly_schedule',
        append,
        order_by: 'id:asc',
      })
      .subscribe(data => {
        this.dataSource.data = data.data;
        this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);
      });
  }

  private processUrlParams(): void {
    const url_per_page = this.activatedRoute.snapshot.queryParamMap.get('per_page');
    if (url_per_page) {
      this.pageSize = parseInt(url_per_page, 10);
    }

    const url_page = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (url_page) {
      this.pageIndex = parseInt(url_page, 10) - 1;
    }
  }

  private listenScopesFormControl(): void {
    this.filterForm
      .get('scopes')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.pageIndex = 0;
        this.fetchData();
      });
  }
}
